import type { CSSProperties } from 'react';
import type { LearningsPageResponse } from '@@/api/learnings/page/page-types';
import type { GridCell } from '@/state/learnings/page-view';
import type { LearningsScreenManager } from '@/state/learnings/useLearningsScreen';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from 'helpers/cn';
import {
  buildGrid,
  changeTypeLabel,
  gridStrength,
  mapRows,
  pageLabel,
  signedPercent,
} from '@/state/learnings/page-view';
import { FIELD_CONTROL, FOCUS_RING, MetricTile, MICRO_LABEL_SHAPE, PillTabs, TableScroll } from '../../ConsolePanel';
import { DeviceFilter, PatternBars } from './EvidenceMap';
import { Section } from './parts';

type GroupBy = 'page' | 'mechanism' | 'element' | 'theme';

const GROUP_OPTIONS = [
  { label: 'Page × change', value: 'page' },
  { label: 'Mechanism', value: 'mechanism' },
  { label: 'Element', value: 'element' },
  { label: 'Theme', value: 'theme' },
];

type EvidenceViewProps = { page: LearningsPageResponse; screen: LearningsScreenManager };

/** Every pattern, grouped four ways, with what is not in the evidence yet. */
export const EvidenceView = ({ page, screen }: EvidenceViewProps) => {
  const [groupBy, setGroupBy] = useState<GroupBy>('page');
  const [hideSingle, setHideSingle] = useState(false);
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();

  return (
    <div className="flex flex-col gap-[14px]">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <PillTabs options={GROUP_OPTIONS} value={groupBy} onChange={(value) => setGroupBy(value as GroupBy)} />
          <DeviceFilter devices={page.devices} device={screen.device} onChange={screen.setDevice} />
        </div>
        <div className="flex flex-wrap items-center gap-3.5">
          <label htmlFor="learnings-hide-single" className="flex cursor-pointer items-center gap-2 text-meta">
            <Checkbox
              id="learnings-hide-single"
              checked={hideSingle}
              onCheckedChange={(value) => setHideSingle(value === true)}
            />
            Hide single-test results
          </label>
          <input
            type="search"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
            aria-label="Search patterns"
            placeholder="Search patterns, tests"
            className={cn(FIELD_CONTROL, FOCUS_RING, 'w-60 border px-3')}
          />
        </div>
      </div>

      <OverlapNotice page={page} />

      {groupBy === 'page' ? (
        <PageGrid page={page} hideSingle={hideSingle} needle={needle} screen={screen} />
      ) : (
        <Section
          title={GROUP_OPTIONS.find((option) => option.value === groupBy)?.label ?? ''}
          description="Pooled lift per pattern, with the adjusted range."
        >
          <PatternBars
            rows={mapRows({ evidence: page.evidence, labels: page.labels, surprises: [], merge: false }).filter(
              (row) =>
                row.grouping === groupBy &&
                (!hideSingle || row.tests > 1) &&
                (!needle || row.label.toLowerCase().includes(needle)),
            )}
            onOpenPattern={screen.openPattern}
            onOpenTest={screen.openTest}
          />
        </Section>
      )}

      <NotInEvidence page={page} onFoundation={screen.openFoundation} />
    </div>
  );
};

type OverlapNoticeProps = { page: LearningsPageResponse };

/** Said once, above the grid: a test that changed several things is counted in several places. */
const OverlapNotice = ({ page }: OverlapNoticeProps) => {
  const most = Object.entries(page.countedIn).sort(([, a], [, b]) => b.length - a.length)[0];
  return (
    <div className="border-warn-line bg-warn-bg flex items-baseline gap-2.5 rounded-lg border px-3 py-[9px]">
      <span className={cn(MICRO_LABEL_SHAPE, 'text-warn')}>Overlap</span>
      <span className="text-meta">
        A test that changed several things counts in several cells. Matching numbers usually mean the same test.
        {most && most[1].length > 2 && ` Test #${most[0]} alone counts in ${most[1].length} patterns.`}
      </span>
    </div>
  );
};

type PageGridProps = {
  page: LearningsPageResponse;
  hideSingle: boolean;
  needle: string;
  screen: LearningsScreenManager;
};

const PageGrid = ({ page, hideSingle, needle, screen }: PageGridProps) => {
  const grid = buildGrid(page.evidence.cells);
  const rows = grid.rows.filter((row) => !needle || changeTypeLabel(row.changeType).toLowerCase().includes(needle));
  const columns = `150px repeat(${Math.max(1, grid.pages.length)}, minmax(120px, 1fr))`;
  const gaps = page.untested.filter((entry) => entry.pageTypes.length > 0).length;

  return (
    <Section
      title="Page by change type"
      description="Pooled lift per cell. Empty cells are untested ground."
      tourAnchor="learnings-grid"
      actions={
        <div className="flex gap-3.5">
          <Swatch style={{ background: 'color-mix(in srgb, hsl(var(--ok)) 18%, transparent)' }}>Positive</Swatch>
          <Swatch style={{ background: 'color-mix(in srgb, hsl(var(--danger)) 18%, transparent)' }}>Negative</Swatch>
          <Swatch className="border-input border border-dashed">1 test</Swatch>
        </div>
      }
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <span className="text-meta">
            {gaps > 0
              ? `${gaps} ${gaps === 1 ? 'change type has' : 'change types have'} been tried on some pages and not others.`
              : 'Every change type tried here has been tried on every page.'}
          </span>
          <Button size="sm" variant="outline" onClick={() => screen.openTab('opportunities')}>
            See untested ideas
          </Button>
        </div>
      }
    >
      <div className="px-3.5 py-3">
        <TableScroll minWidth={150 + grid.pages.length * 125}>
          <div className="grid gap-[5px]" style={{ gridTemplateColumns: columns }}>
            <span />
            {grid.pages.map((pageType) => (
              <span key={pageType} className="micro-label px-2 pb-1">
                {pageLabel(pageType)}
              </span>
            ))}
            {rows.map((row) => (
              <GridRow
                key={row.changeType}
                label={changeTypeLabel(row.changeType)}
                cells={row.cells}
                hideSingle={hideSingle}
                onOpen={screen.openPattern}
              />
            ))}
          </div>
        </TableScroll>
      </div>
    </Section>
  );
};

type GridRowProps = { label: string; cells: GridCell[]; hideSingle: boolean; onOpen: (key: string) => void };

const GridRow = ({ label, cells, hideSingle, onOpen }: GridRowProps) => (
  <>
    <span className="flex items-center text-meta font-medium">{label}</span>
    {cells.map((cell, index) => {
      if (cell.kind === 'untested') {
        return (
          <div
            key={index}
            className="bg-panel2 flex min-h-[46px] flex-col justify-center gap-0.5 rounded-lg px-2 py-1.5"
          >
            <span className="num text-dim">·</span>
            <span className="micro-label text-[9px] tracking-[0.08em]">untested</span>
          </div>
        );
      }
      const single = cell.tests === 1;
      const positive = cell.kind === 'reading' && cell.mean >= 0;
      const tone = positive ? '--ok' : '--danger';
      const style: CSSProperties =
        cell.kind === 'reading'
          ? {
              background: `color-mix(in srgb, hsl(var(${tone})) ${gridStrength(cell.mean)}%, transparent)`,
              borderColor: single ? `hsl(var(${tone}) / 0.45)` : 'transparent',
            }
          : {};
      return (
        <button
          key={index}
          type="button"
          onClick={() => onOpen(cell.key)}
          className={cn(
            'flex min-h-[46px] flex-col justify-center gap-0.5 rounded-lg border px-2 py-1.5 text-left transition-opacity',
            single || cell.kind === 'no-reading' ? 'border-dashed' : 'border-solid',
            cell.kind === 'no-reading' && 'border-input',
            hideSingle && single && 'opacity-25',
            FOCUS_RING,
          )}
          style={style}
        >
          <span
            className={cn(
              'num font-medium',
              cell.kind === 'reading' ? (positive ? 'text-ok' : 'text-danger') : 'text-foreground',
            )}
          >
            {cell.kind === 'reading' ? signedPercent(cell.mean) : 'No reading'}
          </span>
          <span className="micro-label text-[9px] tracking-[0.08em]">
            {cell.tests} {single ? 'test' : 'tests'}
          </span>
        </button>
      );
    })}
  </>
);

type SwatchProps = { children: string; style?: CSSProperties; className?: string };

const Swatch = ({ children, style, className }: SwatchProps) => (
  <span className="supporting inline-flex items-center gap-1.5">
    <span className={cn('size-2.5 rounded-[3px]', className)} style={style} />
    {children}
  </span>
);

type NotInEvidenceProps = { page: LearningsPageResponse; onFoundation: () => void };

/**
 * What the patterns do not stand on yet. A test with a thin foundation is not here: it is counted
 * (decided 2026-10-20), and the foundation check is where to improve it.
 */
const NotInEvidence = ({ page, onFoundation }: NotInEvidenceProps) => {
  const { coverage, status } = page;
  const out =
    status.pending + coverage.leftOut.trackingOrRelease + coverage.leftOut.brokenSplit + coverage.leftOut.noData;
  return (
    <Section
      tourAnchor="learnings-not-in-evidence"
      title="Not in the evidence yet"
      description={`${out} of ${status.concluded} concluded tests`}
      actions={
        <button
          type="button"
          onClick={onFoundation}
          className={cn('text-accent text-meta hover:underline', FOCUS_RING)}
        >
          Foundation check
        </button>
      }
    >
      <div className="grid gap-[7px] p-3.5 [grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
        <MetricTile label="Being read" value={String(status.pending)} />
        <MetricTile
          label="Tracking issue"
          value={String(coverage.leftOut.trackingOrRelease)}
          valueClassName={coverage.leftOut.trackingOrRelease ? 'text-warn' : undefined}
        />
        <MetricTile label="Broken split" value={String(coverage.leftOut.brokenSplit)} />
        <MetricTile label="No data" value={String(coverage.leftOut.noData)} />
        <MetricTile label="No interval stored" value={String(coverage.noInterval)} />
      </div>
    </Section>
  );
};
